import React, { useEffect, useRef, useState } from 'react';
import 'rc-slider/assets/index.css';
import { FormConfig, FormField } from 'components/common/form/FormConfig';
import { FormContent } from 'components/common/form/Form';
import { useVideoOutdoorFields } from './hooks/useVideoOutdoorFields';
import { useImageAudioOutdoorFields } from './hooks/useImageAudioOutdoorFields';
import { useVideoImageOutdoorFields } from './hooks/useVideoImageOutdoorFields';
import { useHtmlOutdoorFields } from './hooks/useHtmlOutdoorFields';
import { useOutdoorBasicFields } from './hooks/useOutdoorBasicFields';
import { OutdoorFormModel, OutdoorFormProps } from './OutdoorFormModel';
import i18n from 'i18n';
import { Trans } from 'react-i18next';
import { FormikFileInputField } from 'components/common/form/field/FileInputField';
import styles from './outdoorForm.module.scss';
import { validateEmpty } from 'utils/ValidateUtils';
import { Currency, LocaleMetaStore } from 'core';
import { useOutdoorBeaconFields } from './hooks/useOutdoorBeaconFields';
import { useOutdoorCreativeSizeModal } from './hooks/useOutdoorCreativeSizeModal';
import { useFormikContext } from 'formik';
import { OutdoorCreativeFields } from 'core/creative/OutdoorCreative';
import { get } from 'lodash';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { toast } from 'react-toastify';

export const OutdoorForm = <T extends OutdoorFormModel<T>>(
  props: OutdoorFormProps<T>
) => {
  const {
    model,
    hintModalData,
    setHintModalData,
    renderHintModal,
    getInitFormConfig
  } = props;

  const [formConfig, setFormConfig] = useState<FormConfig>(getInitFormConfig());

  const { onSizeInstructionBtnClick } = useOutdoorCreativeSizeModal(
    model,
    hintModalData,
    setHintModalData
  );

  const { values, submitCount } = useFormikContext<OutdoorCreativeFields>();
  const outdoorType = get(values, 'typeProperties.outdoorType');

  const outdoorBasicFields = useOutdoorBasicFields(model);
  const videoFormFields = useVideoOutdoorFields(
    model,
    onSizeInstructionBtnClick
  );
  const imageAudioFormFields = useImageAudioOutdoorFields(
    model,
    onSizeInstructionBtnClick
  );
  const videoImageFormFields = useVideoImageOutdoorFields(model);
  const htmlFormFields = useHtmlOutdoorFields(model);
  const beaconFields = useOutdoorBeaconFields(model);
  const localMeta = LocaleMetaStore.getInstance().get();
  const expressPassSettings = localMeta?.property.addonProps.expressPass || {};
  const rawExpressPass = expressPassSettings[model.channelId] !== undefined;
  const nonExpressPassCategories =
    ChannelGroupSetting.fromId(model.channelId).config
      .nonExpressPassCategories ?? [];
  const ineligibleCategory =
    !!values.tenmaxCategory &&
    nonExpressPassCategories.includes(values.tenmaxCategory);
  const isExpressPass = rawExpressPass && !ineligibleCategory;
  const selfCheckLink = model.defaultSelfCheckDocLink(outdoorType);

  // 欄位本身有紅框錯誤，但表單長、容易捲在畫面外，送出時再補一則 toast。
  // 只認遞增：外層 Formik 開了 enableReinitialize，reset 會把 submitCount 歸零。
  const selfCheckDocumentMissing =
    !isExpressPass &&
    !!outdoorType &&
    model.needSelfCheckDocument(outdoorType) &&
    model.isFileFieldEmpty(get(values, 'medias.selfCheckDocument', {}));
  const prevSubmitCount = useRef(submitCount);
  useEffect(() => {
    if (submitCount > prevSubmitCount.current && selfCheckDocumentMissing) {
      toast.error(i18n.t('outdoorForm.errors.selfCheckDocumentRequired'));
    }
    prevSubmitCount.current = submitCount;
  }, [submitCount, selfCheckDocumentMissing]);

  useEffect(() => {
    const {
      neglectedFormFields,
      channelName,
      validateDocument,
      needSelfCheckDocument
    } = model;
    const orderInfoFields = new FormConfig.FieldsBuilder()
      .addFormikInput({
        name: 'typeProperties.orderName',
        label: i18n.t('outdoorForm.labels.orderName'),
        hint: i18n.t('outdoorForm.hints.orderName', { channelName }),
        validate: validateEmpty
      })
      .addFormikInputGroup({
        name: 'typeProperties.orderBudget',
        label: i18n.t('outdoorForm.labels.orderBudget'),
        hint: i18n.t('outdoorForm.hints.orderBudget', { channelName }),
        type: 'number',
        prefix: Currency.NTD,
        validate: validateEmpty
      })
      .addFormikDateRangePicker({
        label: i18n.t('outdoorForm.labels.orderSchedule'),
        name: 'orderSchedule',
        startDateFormikName: 'typeProperties.orderStartDate',
        endDateFormikName: 'typeProperties.orderEndDate',
        format: 'YYYY-MM-DD',
        showTimePicker: false,
        hint: i18n.t('outdoorForm.hints.orderSchedule'),
        validate: (startDate: string, endDate: string) => {
          return validateEmpty(startDate) || validateEmpty(endDate);
        }
      })
      .build();

    const selfCheckDocumentField: FormField = {
      component: FormikFileInputField,
      props: {
        type: 'other',
        label: i18n.t('outdoorForm.labels.selfCheckDocument'),
        name: 'medias.selfCheckDocument',
        validTypes: [
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ],
        hints: [
          i18n.t('outdoorForm.hints.selfCheckFileType'),
          i18n.t('creativeSetupFlow.labels.storageHint', {
            storage: '5MB'
          })
        ],
        permanentHint: selfCheckLink ? (
          <span className={styles.hint}>
            <Trans
              i18nKey='outdoorForm.hints.selfCheckFileExample'
              components={{
                1: (
                  <a href={selfCheckLink} download>
                    Click here
                  </a>
                )
              }}
            />
          </span>
        ) : null,
        validate: (fileData: FileFieldData) =>
          validateDocument(fileData, isExpressPass)
      },
      hide: false
    };

    const basicFieldsWithNonExpressPassHint =
      rawExpressPass && ineligibleCategory
        ? props.basicFields.map((field) =>
            field.props.name === 'tenmaxCategory'
              ? {
                  ...field,
                  props: {
                    ...field.props,
                    hint: (
                      <span className={styles.dangerHint}>
                        {i18n.t('outdoorForm.hints.nonExpressPassCategory')}
                      </span>
                    )
                  }
                }
              : field
          )
        : props.basicFields;

    const formFields: FormField[] = [
      ...basicFieldsWithNonExpressPassHint,
      ...(outdoorType && needSelfCheckDocument(outdoorType)
        ? orderInfoFields
        : []),
      ...outdoorBasicFields,
      ...videoFormFields,
      ...imageAudioFormFields,
      ...videoImageFormFields,
      ...htmlFormFields,
      ...(outdoorType && needSelfCheckDocument(outdoorType)
        ? [selfCheckDocumentField]
        : []),
      ...beaconFields
    ].filter(
      (field) =>
        neglectedFormFields.length === 0 ||
        neglectedFormFields.indexOf(field.props.name) === -1
    );

    setFormConfig(
      new FormConfig.Builder()
        .addSection(
          new FormConfig.SectionBuilder(formFields)
            .withTitle(i18n.t('creativeSetupFlow.labels.creativeBasicInfo'))
            .withHintModal(renderHintModal())
            .build()
        )
        .build()
    );
  }, [
    props.basicFields,
    outdoorType,
    outdoorBasicFields,
    videoFormFields,
    imageAudioFormFields,
    videoImageFormFields,
    htmlFormFields,
    beaconFields,
    model,
    isExpressPass,
    rawExpressPass,
    ineligibleCategory,
    renderHintModal,
    selfCheckLink
  ]);

  return <FormContent formConfig={formConfig} />;
};
